import * as React from "react";
import { Dock as DockGlyph, Home, LockKeyhole, Pencil, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@repo/ui/primitives/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/primitives/command";
import { Label } from "@repo/ui/primitives/label";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/primitives/popover";
import { ScrollArea } from "@repo/ui/primitives/scroll-area";
import { Switch } from "@repo/ui/primitives/switch";

function getPageLabel(page) {
  return [page.grandParent, page.parent, page.title].filter(Boolean).join(" / ");
}

function getPageContext(page) {
  return [page.grandParent, page.parent].filter(Boolean).join(" / ");
}

function groupPages(pages, fallbackLabel) {
  const groups = new Map();

  pages.forEach((page) => {
    const groupLabel = page.parent || page.grandParent || fallbackLabel;
    const group = groups.get(groupLabel) || [];
    group.push(page);
    groups.set(groupLabel, group);
  });

  return Array.from(groups.entries());
}

function DockPagePicker({
  currentUrl,
  onSelect,
  pages,
  quickAccessLabel,
  searchPlaceholder,
  selectedUrls,
  slotIndex,
  noResultsLabel,
  trigger,
}) {
  const [open, setOpen] = React.useState(false);
  const selectablePages = React.useMemo(
    () => pages.filter((page) => page.url === currentUrl || !selectedUrls.has(page.url)),
    [currentUrl, pages, selectedUrls],
  );
  const pageGroups = React.useMemo(
    () => groupPages(selectablePages, quickAccessLabel),
    [quickAccessLabel, selectablePages],
  );

  const handleSelect = (url) => {
    onSelect(slotIndex, url);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(32rem,calc(100vw-2rem))] p-0"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
          <CommandList className="max-h-[min(24rem,50vh)]">
            <CommandEmpty>{noResultsLabel}</CommandEmpty>
            {pageGroups.map(([groupLabel, groupPagesForLabel]) => (
              <CommandGroup key={groupLabel} heading={groupLabel}>
                {groupPagesForLabel.map((page) => {
                  const Icon = page.icon || DockGlyph;
                  const label = getPageLabel(page);
                  const context = getPageContext(page);

                  return (
                    <CommandItem
                      key={page.url}
                      value={`${label} ${page.id || ""} ${page.url || ""}`}
                      onSelect={() => handleSelect(page.url)}
                      className="items-start gap-3 py-2.5"
                    >
                      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{page.title}</span>
                        {context && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {context}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DockSettingsPanel({ preferences, pages = [], onChange }) {
  const { t } = useTranslation("header");
  const pagesByUrl = React.useMemo(
    () => new Map(pages.map((page) => [page.url, page])),
    [pages],
  );
  const selectedUrls = React.useMemo(
    () => new Set(preferences.slots.filter(Boolean)),
    [preferences.slots],
  );
  const selectedSlots = React.useMemo(
    () => preferences.slots
      .map((url, slotIndex) => ({
        currentPage: url ? pagesByUrl.get(url) : null,
        slotIndex,
        url,
      }))
      .filter(({ url }) => Boolean(url)),
    [pagesByUrl, preferences.slots],
  );
  const firstEmptySlot = preferences.slots.findIndex((url) => !url);

  const updateSlot = (slotIndex, value) => {
    const slots = [...preferences.slots];
    slots[slotIndex] = value;
    onChange({ ...preferences, slots });
  };

  const removeSlot = (slotIndex) => updateSlot(slotIndex, null);

  return (
    <ScrollArea className="h-full max-h-[calc(85vh-8rem)] pr-3">
      <div className="space-y-6 pb-4">
        <div className="space-y-2 border-b border-border pb-4">
          <h3 className="flex items-center gap-2 text-lg font-medium text-foreground">
            <DockGlyph className="size-5 text-primary" aria-hidden="true" />
            {t("settings.dock.title", { defaultValue: "Quick Access Dock" })}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("settings.dock.description", { defaultValue: "Add frequently used pages to the dock and place them in your preferred order." })}
          </p>
        </div>

        <div className="flex items-center justify-between gap-6 rounded-[var(--ds-radius-surface)] border border-border bg-muted/40 p-4">
          <div className="space-y-1">
            <Label htmlFor="dashboard-dock-enabled" className="text-sm font-medium">
              {t("settings.dock.enabled.label", { defaultValue: "Show dock" })}
            </Label>
            <p className="text-[0.8rem] text-muted-foreground">
              {t("settings.dock.enabled.description", { defaultValue: "Show the quick access dock at the bottom of the screen." })}
            </p>
          </div>
          <Switch
            id="dashboard-dock-enabled"
            checked={preferences.enabled}
            onCheckedChange={(enabled) => onChange({ ...preferences, enabled })}
            className="shrink-0"
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-foreground">
                {t("settings.dock.slotsTitle", { defaultValue: "Dock shortcuts" })}
              </h4>
              <p className="mt-1 text-[0.8rem] text-muted-foreground">
                {t("settings.dock.slotsDescription", { defaultValue: "Home is fixed. Search for accessible pages to add to the remaining slots." })}
              </p>
              <p className="mt-2 text-xs font-medium text-muted-foreground">
                {t("settings.dock.slotsCount", { defaultValue: "{{count}} of 9 slots used", count: selectedSlots.length })}
              </p>
            </div>

            {firstEmptySlot === -1 ? (
              <span className="shrink-0 pt-1 text-xs text-muted-foreground">
                {t("settings.dock.allSlotsUsed", { defaultValue: "All slots are full" })}
              </span>
            ) : (
              <DockPagePicker
                currentUrl={null}
                onSelect={updateSlot}
                pages={pages}
                quickAccessLabel={t("settings.dock.quickAccessGroup", { defaultValue: "Quick access" })}
                searchPlaceholder={t("settings.dock.searchPlaceholder", { defaultValue: "Search pages..." })}
                selectedUrls={selectedUrls}
                slotIndex={firstEmptySlot}
                noResultsLabel={t("settings.dock.noPagesFound", { defaultValue: "No matching pages found." })}
                trigger={(
                  <Button type="button" variant="outline" size="sm" className="shrink-0">
                    <Plus className="size-4" aria-hidden="true" />
                    {t("settings.dock.addShortcut", { defaultValue: "Add shortcut" })}
                  </Button>
                )}
              />
            )}
          </div>

          <div className="rounded-[var(--ds-radius-surface)] border border-border bg-muted/20 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex min-h-16 items-center gap-3 rounded-[var(--ds-radius-control)] border border-border bg-muted/35 p-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-[var(--ds-radius-control)] bg-primary text-primary-foreground">
                  <Home className="size-4" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("settings.dock.slotLabel", { defaultValue: "Slot {{number}}", number: 1 })}
                  </p>
                  <p className="truncate text-sm font-medium text-foreground">
                    {t("settings.dock.home", { defaultValue: "Home" })}
                  </p>
                </div>
                <LockKeyhole
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-label={t("settings.dock.fixed", { defaultValue: "Fixed slot" })}
                />
              </div>

              {selectedSlots.map(({ currentPage, slotIndex, url }) => {
                const Icon = currentPage?.icon || DockGlyph;
                const context = currentPage
                  ? getPageContext(currentPage)
                  : t("settings.dock.unavailablePageDescription", { defaultValue: "This page is no longer available." });

                return (
                  <div
                    key={slotIndex}
                    className="flex min-h-16 items-center gap-3 rounded-[var(--ds-radius-control)] border border-border bg-card p-3"
                  >
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-[var(--ds-radius-control)] bg-muted text-muted-foreground">
                      <Icon className="size-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        {t("settings.dock.slotLabel", { defaultValue: "Slot {{number}}", number: slotIndex + 2 })}
                      </p>
                      <p className="truncate text-sm font-medium text-foreground">
                        {currentPage?.title || t("settings.dock.unavailablePage", { defaultValue: "Unavailable page" })}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{context}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <DockPagePicker
                        currentUrl={url}
                        onSelect={updateSlot}
                        pages={pages}
                        quickAccessLabel={t("settings.dock.quickAccessGroup", { defaultValue: "Quick access" })}
                        searchPlaceholder={t("settings.dock.searchPlaceholder", { defaultValue: "Search pages..." })}
                        selectedUrls={selectedUrls}
                        slotIndex={slotIndex}
                        noResultsLabel={t("settings.dock.noPagesFound", { defaultValue: "No matching pages found." })}
                        trigger={(
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("settings.dock.changeShortcut", { defaultValue: "Change shortcut {{number}}", number: slotIndex + 2 })}
                            className="size-[var(--ds-density-icon-button-size)] rounded-[var(--ds-radius-control)] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </Button>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("settings.dock.removeShortcut", { defaultValue: "Remove shortcut {{number}}", number: slotIndex + 2 })}
                        onClick={() => removeSlot(slotIndex)}
                        className="size-[var(--ds-density-icon-button-size)] rounded-[var(--ds-radius-control)] text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
